import close from "./images/close";

export interface NoticeOptions {
	/** Signal that can be used to forcefully close the notice. */
	readonly signal?: AbortSignal;
	readonly timeoutMs?: number;
}

const EMPTY_OBJECT = Object.freeze({});

/* Takes an element with WooCommerce-generated notices (recognized by having
 * a CSS class of `woocommerce-message` or `woocommerce-error`) and shows all
 * notices contained within sequentially.
 */
export async function showWooCommerceNotices(element: HTMLElement) {
	const notices = (isTemplate(element) ? element.content : element)
		.querySelectorAll(".woocommerce-message, .woocommerce-error");

	for (const notice of notices) {
		/* NOTE Trimming for nicer console message; extra whitespace doesn't
		 * affect HTML.
		 */
		const textContent = (notice.textContent ?? "").trim();
		if (notice.classList.contains("woocommerce-error")) {
			await noticeFailure(textContent);
		} else {
			await noticeSuccess(textContent);
		}
	}
}

export function noticeSuccess(message: string, options: NoticeOptions = EMPTY_OBJECT) {

	console.log(`[\x1B[32mNOTICE\x1B[0m] ${message}`);

	return makeNotice({
		className: "gct-notice gct-notice-success gct-flex-row gct-flex-space-between",
		message,
		signal: options.signal,
		timeoutMs: options.timeoutMs,
	});
}

export function noticeFailure(error: string | Error, options: NoticeOptions = EMPTY_OBJECT) {
	if (typeof error === "string") {
		error = new Error(error);
	}

	console.error(`[\x1B[31mNOTICE\x1B[0m] ${error.message}`);
	console.trace(error);

	return makeNotice({
		className: "gct-notice gct-notice-failure gct-flex-row gct-flex-space-between",
		message: error.message,
		signal: options.signal,
		timeoutMs: options.timeoutMs,
	});
}

namespace makeNotice {
	export interface Props {
		readonly className: string;
		readonly message: string;
		readonly signal?: AbortSignal;
		readonly timeoutMs?: number;
	}
}

function makeNotice({
	className,
	message,
	signal,
	timeoutMs = 4000,
}: makeNotice.Props) {

	let resolve: () => void, reject: (error: Error) => void;
	const promise = new Promise<void>((_resolve, _reject) => {
		resolve = _resolve;
		reject = _reject;
	});

	const closeButton = <button
		type="button"
		className="gct-btn-tertiary"
		innerHTML={close}
		style={{ color: "var(--neutral-200)" }}
	/>;

	const element = (
		<div className={className}>
			{message}
			{closeButton}
		</div>
	);

	document.body.appendChild(element);
	element.classList.add("gct-notice-entry");
	getComputedStyle(element);
	setTimeout(() => element.classList.remove("gct-notice-entry"), 0);

	const onClick = () => {
		cleanup();
		resolve();
	};

	const onAbort = () => {
		cleanup();
		reject(new Error("Aborted"));
	};

	const onTimeout = () => {
		cleanup();
		resolve();
	};

	const cleanup = () => {
		element.remove();
		clearTimeout(timeout);
		signal?.removeEventListener("abort", onAbort);
		closeButton.removeEventListener("click", onClick);
	}

	const timeout = setTimeout(onTimeout, timeoutMs);
	signal?.addEventListener("abort", onAbort);
	closeButton?.addEventListener("click", onClick);

	return promise;
}

function isTemplate(element: HTMLElement): element is HTMLTemplateElement {
	return element.tagName === "TEMPLATE";
}
